import { Copy } from '@styled-icons/fa-solid/Copy';
import * as S from 'components/AuthError/AuthError.style';
import exclamationMark from 'components/AuthError/auth_error_exclamation_mark.png';
import { ErrorDetails } from 'models/context/error-details';
import { ToastContainer } from 'react-toastify';
import { Notification } from 'services/notification-service';
import React, { useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Icons } from '@social-solutions/component-library';

export interface IAuthErrorMessage {
    errorCode: string | null;
    jsonParams: Record<string, unknown>;
    errorDetails: ErrorDetails | null;
}

const AuthErrorMsg = ({ errorCode, jsonParams, errorDetails }: IAuthErrorMessage ): React.ReactElement => {
    const { t } = useTranslation();
    const errMessage = errorCode !== null ? t(`systemErrors:${errorCode}`, jsonParams) : t('public:UNRECOGNIZED_EXCEPTION');
    const [showErrorDetails, setShowErrorDetails] = useState(false);

    const toggleErrorDetails = () => {
        setShowErrorDetails(!showErrorDetails);
    };

    //a temporary browser-safe solution to only display properties we know we want to display to the user. Can be removed once we have proper JSON Schema + quicktype validation in place
    const safeErrorDetailsProps = [
        'eventId',
        'srcctx',
        'react_error',
        'request_start',
        'request_end',
        'request_duration'
    ];

    const safeErrorDetails: Record<string, unknown> = {};

    if (errorDetails !== null) {
        for (const prop of safeErrorDetailsProps) {
            if (errorDetails[prop]) {
                safeErrorDetails[prop] = errorDetails[prop];
            }
        }
    }

    safeErrorDetails.error_code = errorCode;
    if (jsonParams?.error_details) {
        safeErrorDetails.error_details = jsonParams.error_details;
    }

    const copiedNotification: Notification = useMemo(() => new Notification(t('notifications:NOTIFY_COPIED'), 'copied-notification', { type: 'info', autoClose: 2000 }),[t]);

    const onCopied = () => {
        copiedNotification.show();
    };
    const errorMessageRef = useRef<HTMLPreElement | null>(null);
    const copyErrorMessageToClipboard = () => {
        const errorMessageText = JSON.stringify(safeErrorDetails, null, 2);
        if (errorMessageText) {
            navigator.clipboard.writeText(errorMessageText).then(() => {
                onCopied();
            });
        }
    };

    return (
        <S.Content>
            <ToastContainer
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                pauseOnHover
            />
            <S.CenteredCard>
                <S.ExclamationMark src={exclamationMark} alt="large alerting exclamation mark" />
                <S.ErrorMessage>
                    <Trans>{errMessage}</Trans>
                </S.ErrorMessage>
                {errorDetails && (
                    <S.ErrorDetailsContainer>
                        {showErrorDetails ? (
                            <S.ErrorDetailsContent>
                                <S.ShowErrorButton onClick={toggleErrorDetails}>
                                    <S.ExpandOrCollapseIcon as={Icons.Collapse}/> Hide Error Details
                                </S.ShowErrorButton>
                                <S.ErrorMessageContainer>
                                    <pre ref={errorMessageRef}>{JSON.stringify(safeErrorDetails, null, 2)}</pre>
                                </S.ErrorMessageContainer>
                            </S.ErrorDetailsContent>
                        ) : (
                            <S.ErrorDetailsContent>
                                <S.ShowErrorButton onClick={toggleErrorDetails}>
                                    <S.ExpandOrCollapseIcon as={Icons.Expand} /> Show Error Details
                                </S.ShowErrorButton>
                            </S.ErrorDetailsContent>
                        )}
                        <S.CopyErrorContainer>
                            {/* Hiding this link via comment. There's some security concerns around providing a link that comes from a query parameter. Not sure how to tackle this safely yet. */}
                            {/* <S.ReturntoLogInLink href="/login">
                                <ChevronLeft size={S.iconSizes.chevronLeft}/>
                                Return to Log In
                            </S.ReturntoLogInLink> */}
                            <S.CopyErrorButton onClick={copyErrorMessageToClipboard}>
                                <Copy size={S.iconSizes.clipboardCopy} /> Copy Error
                            </S.CopyErrorButton>
                        </S.CopyErrorContainer>
                    </S.ErrorDetailsContainer>
                )}
            </S.CenteredCard>
        </S.Content>

    );
};

export default AuthErrorMsg;
